import { ProductPlanStatus } from "../components/Member/CurrentPlan/constants";
import {
  MemberProduct,
  MemberProductLevelEnum,
  PayChannel,
} from "../features/api/memberApi";

const PAY_CYCLES = {
  MONTH: "MONTH",
  YEAR: "YEAR",
  WEEK: "WEEK",
  LIFETIME: "LIFETIME",
} as const;

type PayCycle = (typeof PAY_CYCLES)[keyof typeof PAY_CYCLES];

const IAP_CHANNELS = new Set<PayChannel>([
  PayChannel.APPLE_IAP,
  PayChannel.GOOGLE_IAP,
]);

export type CompletePlansByType = {
  weekly?: MemberProduct;
  monthly?: MemberProduct;
  yearly?: MemberProduct;
  lifetime?: MemberProduct;
};

const hasActivePaymentConfig = (
  plan: MemberProduct,
  matchChannel: (channel: PayChannel) => boolean,
): boolean =>
  plan.status === ProductPlanStatus.ACTIVE &&
  !!plan.payment_configs?.some(
    (config) =>
      matchChannel(config.channel as PayChannel) &&
      config.status === ProductPlanStatus.ACTIVE,
  );

/**
 * 返回产品当前可用的支付渠道（仅 ACTIVE 配置；IAP 渠道对 Web 端不可用，已过滤）。
 * PayPal 按钮显隐的唯一数据来源 —— 禁止前端硬编码套餐-渠道映射。
 */
export const getAvailableChannels = (plan: MemberProduct): PayChannel[] => {
  if (plan.status !== ProductPlanStatus.ACTIVE) {
    return [];
  }
  return [
    ...new Set(
      (plan.payment_configs ?? [])
        .filter((config) => config.status === ProductPlanStatus.ACTIVE)
        .map((config) => config.channel as PayChannel)
        .filter((channel) => !IAP_CHANNELS.has(channel)),
    ),
  ];
};

/** 便捷判定：产品是否支持某渠道（如 hasChannel(plan, PayChannel.PAYPAL)）。 */
export const hasChannel = (plan: MemberProduct, channel: PayChannel): boolean =>
  getAvailableChannels(plan).includes(channel);

export const filterActiveMemberPlans = (
  data: MemberProduct[],
  {
    isApp,
    payChannel,
  }: {
    isApp: boolean;
    payChannel: PayChannel;
  },
): MemberProduct[] => {
  if (isApp) {
    return data.filter((plan) =>
      hasActivePaymentConfig(plan, (channel) => channel === payChannel),
    );
  }

  return data.filter((plan) =>
    hasActivePaymentConfig(plan, (channel) => !IAP_CHANNELS.has(channel)),
  );
};

const findExpertPlanByCycle = (
  activePlans: MemberProduct[],
  payCycle: PayCycle,
): MemberProduct | undefined =>
  activePlans.find(
    (plan) =>
      plan.level === MemberProductLevelEnum.EXPERT &&
      plan.pay_cycle === payCycle,
  );

const sanitizePlanEquities = (plan: MemberProduct): MemberProduct => {
  if (!plan.equities) {
    return plan;
  }

  return {
    ...plan,
    equities: plan.equities.filter(
      (equity: { status?: string }) =>
        equity?.status === "OWN" || equity?.status === "",
    ),
  };
};

const VALID_PAY_CYCLES = new Set<string>(Object.values(PAY_CYCLES));

export const buildSubscriptionPlans = (
  data: MemberProduct[],
  activePlans: MemberProduct[],
  billingCycle: string,
): MemberProduct[] => {
  if (!VALID_PAY_CYCLES.has(billingCycle)) {
    return [];
  }

  const payCycle = billingCycle as PayCycle;

  const freePlan = data.find(
    (plan) => plan.level === MemberProductLevelEnum.FREE,
  );
  const completePlan = findExpertPlanByCycle(activePlans, payCycle);

  return [freePlan, completePlan]
    .filter((plan): plan is MemberProduct => !!plan)
    .map(sanitizePlanEquities);
};

const toSanitizedExpertPlan = (
  activePlans: MemberProduct[],
  payCycle: PayCycle,
): MemberProduct | undefined => {
  const plan = findExpertPlanByCycle(activePlans, payCycle);
  return plan ? sanitizePlanEquities(plan) : undefined;
};

export const resolveCompletePlansByType = (
  activePlans: MemberProduct[],
): CompletePlansByType => ({
  weekly: toSanitizedExpertPlan(activePlans, PAY_CYCLES.WEEK),
  monthly: toSanitizedExpertPlan(activePlans, PAY_CYCLES.MONTH),
  yearly: toSanitizedExpertPlan(activePlans, PAY_CYCLES.YEAR),
  lifetime: toSanitizedExpertPlan(activePlans, PAY_CYCLES.LIFETIME),
});

export const resolveSubscriptionPlansFromData = (
  data: MemberProduct[],
  billingCycle: string,
  {
    isApp,
    payChannel,
  }: {
    isApp: boolean;
    payChannel: PayChannel;
  },
): MemberProduct[] => {
  const activePlans = filterActiveMemberPlans(data, { isApp, payChannel });
  return buildSubscriptionPlans(data, activePlans, billingCycle);
};

export const resolveCompletePlansFromData = (
  data: MemberProduct[],
  {
    isApp,
    payChannel,
  }: {
    isApp: boolean;
    payChannel: PayChannel;
  },
): CompletePlansByType => {
  const activePlans = filterActiveMemberPlans(data, { isApp, payChannel });
  return resolveCompletePlansByType(activePlans);
};
